import { useEffect, useRef, useState } from "react";
import { toast } from "@/components/ui/sonner";
import { useTranslation } from "react-i18next";
import { Button } from "@akuna/ui";
import { Camera, Upload, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { isNative, pickPhoto } from "@/lib/native";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface PetPhotoUploadProps {
  value?: File | string | null;
  onChange: (file: File | null) => void;
  disabled?: boolean;
  allowReplace?: boolean;
}

export function PetPhotoUpload({
  value,
  onChange,
  disabled,
  allowReplace = false,
}: PetPhotoUploadProps) {
  const { t } = useTranslation("patients");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const syncPreview = async () => {
      if (typeof value === "string") {
        setPreview(value);
        return;
      }

      if (!value) {
        setPreview(null);
        return;
      }

      if (value instanceof File) {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (cancelled) return;
          setPreview(reader.result as string);
        };
        reader.readAsDataURL(value);
      }
    };

    void syncPreview();

    return () => {
      cancelled = true;
    };
  }, [value]);

  // Validates and applies a selected image file (preview + onChange). Shared by
  // the web file input and the native photo picker.
  const applySelectedFile = (file: File) => {
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type)) {
      setTimeout(() => {
        toast.error(t("petPhoto.invalidType"));
      }, 0);
      return;
    }

    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      setTimeout(() => {
        toast.error(t("petPhoto.tooLarge"));
      }, 0);
      return;
    }

    onChange(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    applySelectedFile(file);
  };

  // On native, both "choose file" and "take photo" open the native picker,
  // which lets the user choose between camera and gallery.
  const handleNativePick = async () => {
    if (disabled) return;
    const file = await pickPhoto();
    if (file) applySelectedFile(file);
  };

  const handleChooseFileClick = () => {
    if (isNative()) {
      void handleNativePick();
      return;
    }
    fileInputRef.current?.click();
  };

  const handleTakePhotoClick = () => {
    if (disabled) return;
    if (isNative()) {
      void handleNativePick();
      return;
    }
    setCameraError(null);
    setIsCameraOpen(true);
  };

  const handleRemove = () => {
    onChange(null);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
    stopCamera();
  };

  const stopCamera = () => {
    setCameraError(null);
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    setIsCameraOpen(false);
  };

  useEffect(() => {
    if (!isCameraOpen) {
      stopCamera();
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError(t("petPhoto.cameraUnsupported"));
      return;
    }

    let isMounted = true;
    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
        });
        if (!isMounted) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        setCameraStream(stream);
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {
            /* ignore autoplay issues */
          });
        }
      } catch (error) {
        console.error("Erro ao acessar a câmera:", error);
        setCameraError(t("petPhoto.cameraPermission"));
      }
    };

    startCamera();

    return () => {
      isMounted = false;
      if (cameraStream) {
        cameraStream.getTracks().forEach((track) => track.stop());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCameraOpen]);

  const handleCapture = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      setCameraError(t("petPhoto.cameraInitializing"));
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext("2d");

    if (!context) {
      setCameraError(t("petPhoto.captureFailed"));
      return;
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setCameraError(t("petPhoto.processFailed"));
          return;
        }

        const file = new File([blob], `pet-photo-${Date.now()}.jpg`, {
          type: blob.type || "image/jpeg",
        });

        // Validar tamanho (5MB)
        const MAX_FILE_SIZE = 5 * 1024 * 1024;
        if (file.size > MAX_FILE_SIZE) {
          setTimeout(() => {
            toast.error(t("petPhoto.tooLarge"));
          }, 0);
          stopCamera();
          return;
        }

        onChange(file);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
        setPreview(dataUrl);
        if (fileInputRef.current) fileInputRef.current.value = "";
        if (cameraInputRef.current) cameraInputRef.current.value = "";
        stopCamera();
      },
      "image/jpeg",
      0.92
    );
  };

  return (
    <div className="space-y-4">
      {/* Inputs sempre no DOM para que possam ser acessados pelo ref */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={handleFileChange}
        className="hidden"
        disabled={disabled}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        className="hidden"
        disabled={disabled}
      />

      {preview ? (
        <div className="space-y-3">
          <div className="relative w-full max-w-sm mx-auto">
            <img
              src={preview}
              alt={t("petPhoto.preview")}
              className="w-full h-64 object-cover rounded-lg border"
            />
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute top-2 right-2"
              onClick={handleRemove}
              disabled={disabled}
              aria-label={t("petPhoto.remove")}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {allowReplace ? (
            <div className="flex flex-col sm:flex-row gap-2 max-w-sm mx-auto">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={handleChooseFileClick}
                disabled={disabled}
              >
                <Upload className="h-4 w-4 mr-2" />
                {t("petPhoto.change")}
              </Button>

              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={handleTakePhotoClick}
                disabled={disabled}
              >
                <Camera className="h-4 w-4 mr-2" />
                {t("petPhoto.retake")}
              </Button>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={handleChooseFileClick}
            disabled={disabled}
          >
            <Upload className="h-4 w-4 mr-2" />
            {t("petPhoto.chooseFile")}
          </Button>

          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={handleTakePhotoClick}
            disabled={disabled}
          >
            <Camera className="h-4 w-4 mr-2" />
            {t("petPhoto.takePhoto")}
          </Button>
        </div>
      )}
      <Dialog
        open={isCameraOpen}
        onOpenChange={(open) => {
          if (!open) stopCamera();
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("petPhoto.captureTitle")}</DialogTitle>
            <DialogDescription>
              {t("petPhoto.captureDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {cameraError ? (
              <p className="text-sm text-destructive">{cameraError}</p>
            ) : (
              <div className="aspect-video w-full overflow-hidden rounded-md border bg-black">
                <video
                  ref={videoRef}
                  className="h-full w-full object-cover"
                  playsInline
                  muted
                  autoPlay
                />
              </div>
            )}
          </div>
          <DialogFooter className="flex flex-col sm:flex-row sm:justify-between gap-2">
            <Button
              type="button"
              variant="outline"
              className="w-full sm:w-auto"
              onClick={stopCamera}
            >
              {t("petPhoto.cancel")}
            </Button>
            <Button
              type="button"
              className="w-full sm:w-auto"
              onClick={handleCapture}
              disabled={!!cameraError || !cameraStream}
            >
              {t("petPhoto.capture")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <p className="text-xs text-muted-foreground text-center">
        {t("petPhoto.help")}
      </p>
    </div>
  );
}
